import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { Menu, X, Globe, UserRound } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { ukFlag, italyFlag } from '../../../assets/flag/FlagIcon';
import { selectToken, selectUser } from '../../../features/auth/authSlice';
import { getRoleDashboardLink } from '../../../utils/getRoleDashboardLink';

const NavbarTemplate = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const langRef = useRef(null);
  const { i18n, t } = useTranslation();
  const token = useSelector(selectToken);
  const user = useSelector(selectUser);

  const isLoggedIn = !!token;
  const roleDashboard = isLoggedIn ? getRoleDashboardLink(user?.role, t) : null;

  useOutsideClick(langRef, () => setLangOpen(false));

  const languages = [
    { code: 'en', name: 'English', flag: ukFlag, nativeName: 'English' },
    { code: 'it', name: 'Italian', flag: italyFlag, nativeName: 'Italiano' },
  ];

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const navigation = [
    { name: t('navbar.links.home'), href: '/' },
    { name: t('navbar.links.findSurgeons'), href: '/surgeons' },
    { name: t('navbar.links.blog'), href: '/blog' },
    { name: t('navbar.links.about'), href: '/about' },
    { name: t('navbar.links.faq'), href: '/faq' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const renderAuthButton = (onClick, fullWidth = false) => {
    const layoutClass = fullWidth
      ? 'flex w-full items-center justify-center gap-2'
      : 'flex items-center gap-2';

    if (!isLoggedIn) {
      return (
        <Link
          to="/auth/register"
          onClick={onClick}
          className={`${layoutClass} rounded-full bg-[#026CB6] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2783d3]`}
        >
          {t('navbar.joinButton')}
          <UserRound className="h-4 w-4" />
        </Link>
      );
    }

    if (!roleDashboard) {
      return null;
    }

    return (
      <Link
        to={roleDashboard.path}
        onClick={onClick}
        className={`${layoutClass} rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
          isActive(roleDashboard.path)
            ? 'bg-[#025a9c] text-white'
            : 'bg-[#026CB6] text-white hover:bg-[#2783d3]'
        }`}
      >
        {roleDashboard.label}
        <UserRound className="h-4 w-4" />
      </Link>
    );
  };

  const renderJoinUserButton = (onClick, fullWidth = false) => {
    if (isLoggedIn) {
      return null;
    }

    const layoutClass = fullWidth ? 'flex w-full items-center justify-center' : 'flex items-center';

    return (
      <Link
        to="/auth/login"
        onClick={onClick}
        className={`${layoutClass} rounded-full border border-[#026CB6] px-5 py-2 text-sm font-semibold text-[#026CB6] transition-colors hover:bg-[#026CB6] hover:text-white`}
      >
        {t('navbar.joinUserButton')}
      </Link>
    );
  };

  return (
    <nav className="relative sticky top-0 z-50 bg-white py-2 shadow">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex flex-col items-center">
            <img
              src="/image/logo.png"
              alt="TrustSurgery logo"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width="96"
              height="96"
              className="h-auto w-16 md:w-24"
            />
          </Link>

          <div className="hidden items-center space-x-6 lg:flex">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`font-semibold transition-colors ${
                  isActive(item.href)
                    ? 'border-b-2 border-[#8abbdf] text-[#026CB6]'
                    : 'text-gray-500 hover:text-[#026CB6]'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          <div className="hidden items-center space-x-3 lg:flex">
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                aria-label={t('navbar.languageSwitcher', { defaultValue: 'Switch language' })}
                aria-expanded={langOpen}
                aria-haspopup="listbox"
                className="flex items-center gap-1.5 rounded-md px-3 py-2 text-gray-500 transition-colors hover:text-blue-600"
              >
                <Globe className="h-4 w-4 text-gray-500" />
                <span>{currentLanguage.nativeName}</span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {langOpen && (
                <div className="absolute left-0 z-50 mt-2 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => {
                        i18n.changeLanguage(language.code);
                        setLangOpen(false);
                      }}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${
                        i18n.language === language.code
                          ? 'bg-blue-50 text-[#026CB6]'
                          : 'text-gray-700'
                      }`}
                    >
                      <span className="text-lg">{language.flag}</span>
                      <span className="flex-1 font-medium">{language.nativeName}</span>
                      {i18n.language === language.code && (
                        <Check className="h-4 w-4 text-[#026CB6]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {renderJoinUserButton()}
              {renderAuthButton()}
            </div>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label={
              isOpen
                ? t('common.close', { defaultValue: 'Close menu' })
                : t('common.open', { defaultValue: 'Open menu' })
            }
            aria-expanded={isOpen}
            aria-controls="mobile-nav-menu"
            className="text-gray-600 transition-colors hover:text-gray-900 focus:outline-none lg:hidden"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        <div
          id="mobile-nav-menu"
          className={`absolute top-full right-0 left-0 transition-all duration-300 ease-in-out lg:hidden ${
            isOpen
              ? 'pointer-events-auto translate-y-0 opacity-100'
              : 'pointer-events-none -translate-y-2 opacity-0'
          }`}
        >
          <div className="border-t border-gray-100 bg-white pb-4 shadow-lg">
            <div className="flex flex-col space-y-1 px-4 pt-2 sm:px-6 lg:px-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'
                  }`}
                >
                  {item.name}
                </Link>
              ))}

              <div className="mt-3 flex items-center justify-center gap-3">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => i18n.changeLanguage(language.code)}
                    className={`flex items-center gap-2 rounded-lg border px-4 py-2 transition-colors ${
                      i18n.language === language.code
                        ? 'border-[#026CB6] bg-blue-50 text-[#026CB6]'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    <span className="text-xl leading-none">{language.flag}</span>
                    <span className="text-sm font-semibold">{language.nativeName}</span>
                    {i18n.language === language.code && (
                      <Check className="h-4 w-4 text-[#026CB6]" />
                    )}
                  </button>
                ))}
              </div>

              <div className="mt-3">
                <div className="mb-2">{renderJoinUserButton(() => setIsOpen(false), true)}</div>
                {renderAuthButton(() => setIsOpen(false), true)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavbarTemplate;
